import { Children, isValidElement, useMemo } from "react";
import { useUIDSeed } from "react-uid";
import styled from "@emotion/styled";
import PropTypes from "prop-types";

import { withErrorBoundary } from "../../../ErrorBoundary";
import { Error } from "../Error";
import { Help } from "../Help";
import { Label, labelSizes, labelVariants } from "../Label";
import styles from "./RadioGroup.style";

const RadioGroupStyled = styled.div((props) => ({ ...styles(props) }));

const RadioGroup = ({
    labelAlignLeft,
    labelWidth,
    label,
    labelComponent,
    labelIconComponent,
    children,
    required,
    error,
    errorMessage,
    helpText,
    size,
    isRadioCard,
    className,
    ...rest
}) => {
    const radioElements = useMemo(
        () => Children.toArray(children).filter((child) => isValidElement(child)),
        [children],
    );

    const uid = useUIDSeed();

    if (!radioElements.length) {
        return null;
    }

    return (
        <RadioGroupStyled
            labelAlignLeft={labelAlignLeft}
            labelWidth={labelWidth}
            size={size}
            hasLabel={label}
            isRadioCard={isRadioCard}
            className={className}
            {...rest}
        >
            <Label
                className="radioGroup_label"
                label={label}
                labelComponent={labelComponent}
                iconComponent={labelIconComponent}
                error={error}
                required={required}
                variant={labelVariants.PRIMARY}
                size={labelSizes[size?.toUpperCase()]}
                width={labelWidth}
                alignLeft={labelAlignLeft}
            />
            <div className="radioGroup_outer">
                <ul className="radioGroup_list">
                    {radioElements.map((child) => (
                        <li key={uid(child)}>{child}</li>
                    ))}
                </ul>
                <Error error={error} errorMessage={errorMessage} className="radioGroup_error" />
                <Help text={helpText} className="radioGroup_help" />
            </div>
        </RadioGroupStyled>
    );
};

const radioGroupSizes = Object.freeze({
    SMALL: "small",
    NORMAL: "normal",
    LARGE: "large",
});

RadioGroup.propTypes = {
    labelAlignLeft: PropTypes.bool,
    labelWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    labelComponent: PropTypes.node,
    labelIconComponent: PropTypes.node,
    required: PropTypes.bool,
    error: PropTypes.bool,
    errorMessage: PropTypes.string,
    helpText: PropTypes.string,
    size: PropTypes.oneOf([...Object.values(radioGroupSizes)]),
    isRadioCard: PropTypes.bool,
    className: PropTypes.string,
};

RadioGroup.defaultProps = {
    labelAlignLeft: false,
    labelWidth: undefined,
    label: undefined,
    labelComponent: undefined,
    labelIconComponent: undefined,
    required: false,
    error: false,
    errorMessage: undefined,
    helpText: undefined,
    size: radioGroupSizes.NORMAL,
    isRadioCard: false,
    className: undefined,
};

export { radioGroupSizes };

export default withErrorBoundary(RadioGroup);
